import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import DataTable from "../../components/RestaurantAdminComponents/DataTable";
import SecondaryButton from "../../components/Common/SecondaryButton";
import FilterToolbar from "../../components/Common/FilterToolbar";
import { IconAvatar } from "../../components/Common/PageWidgets";
import { get } from "../../api/axios";
import endpoints from "../../api/endpoints";

import coinIcon from "../../assets/images/user-dashboard/voucher.svg";
import starIcon from "../../assets/images/user-dashboard/star.svg";
import WhiteArrow from "../../assets/images/user-dashboard/white-right-arrow.svg";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/* -------------------------------------------------------------------------
 * AdminDashboardPage — "Dashboard" overview.
 *
 * Per the original file's own convention this is content-only (no sidebar,
 * no top app-bar) — the dark sidebar nav and the notification bell / admin
 * profile menu shown in the wireframe belong to the parent Layout, not this
 * page. Only the in-page period selector is built here.
 *
 * Endpoints reused from the previous version (unchanged):
 *   adminDashboardListingCounts, adminDashboardLinkedCardsStat,
 *   adminDashboardPartnerPerformanceBreakdown, adminSettlementsDashboard
 *
 * Endpoints reused from the Settlements dashboard page (already real,
 * just not previously called from here):
 *   adminRestaurantsPlatformFeesSummaryByStatus → powers Pending/Completed
 *     Settlements (it already returns a paid/pending/failed breakdown)
 *   adminSettlementsDashboard → Recent Settlement Runs table via graph rows
 *
 * New endpoints this page needs that don't exist yet — each renders a
 * plain "awaiting API" state instead of invented numbers, and is marked
 * TODO(api) at the call site:
 *   - a daily/weekly time-series endpoint for the 3 trend charts
 * ---------------------------------------------------------------------- */

/* ============ helpers ============ */

const pick = (obj, ...paths) => {
  for (const path of paths) {
    const value = path
      .split(".")
      .reduce((acc, key) => (acc === null || acc === undefined ? acc : acc[key]), obj);
    if (value !== undefined && value !== null) return value;
  }
  return null;
};

const extractPayload = (queryData) => {
  if (!queryData) return null;
  if (typeof queryData.data?.data !== "undefined") return queryData.data.data;
  return queryData.data || null;
};

const currency = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  if (Number.isNaN(num)) return fallback;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const number = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(value);
  if (Number.isNaN(num)) return fallback;
  return num.toLocaleString();
};

const pct = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num;
};

const formatDateShort = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const stripHtmlTags = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const cleanActivityText = (value, fallback = "") => {
  const text = stripHtmlTags(value).trim();
  if (!text || text.toLowerCase() === "default" || text.includes("App\\Models")) return fallback;
  return text;
};



const PERIOD_LABELS = {
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
  all_time: "All Time",
  custom: "Custom Range",
};

const getPeriodLabel = (period) => {
  const labels = {
    weekly: "Weekly",
    monthly: "Monthly",
    yearly: "Yearly",
    all_time: "All Time",
    custom: "Custom Range",
  };
  return labels[period] || "Weekly";
};

/* ============ inline icons (self-contained, no new asset files needed) ============ */

const sp = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
const IconUsers = () => (<svg {...sp}><circle cx="9" cy="8" r="3" /><path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6" /><circle cx="17" cy="9" r="2.4" /><path d="M17.5 14.2c2.6.4 4.5 2.6 4.5 5.3" /></svg>);
const IconCard = () => (<svg {...sp}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>);
const IconBuilding = () => (<svg {...sp}><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" /></svg>);
const IconReceipt = () => (<svg {...sp}><path d="M6 3h12v18l-2-1-2 1-2-1-2 1-2-1-2 1z" /><path d="M9 8h6M9 12h6" /></svg>);
const IconWallet = () => (<svg {...sp}><path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" /><path d="M4 6v12a2 2 0 0 0 2 2h14v-4" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></svg>);
const IconPercent = () => (<svg {...sp}><circle cx="7" cy="7" r="2.3" /><circle cx="17" cy="17" r="2.3" /><path d="M18 6 6 18" /></svg>);
const IconClock = () => (<svg {...sp}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>);
const IconCheckCircle = () => (<svg {...sp}><circle cx="12" cy="12" r="9" /><path d="m8.5 12.5 2.5 2.5 5-5" /></svg>);
const IconCalendar = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>);
const IconChevronDown = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>);
const IconInfo = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>);

/* ============ small shared bits ============ */

const StatusBadge = ({ status }) => {
  const map = {
    paid: "adb-badge--green",
    completed: "adb-badge--green",
    active: "adb-badge--green",
    pending: "adb-badge--orange",
    failed: "adb-badge--red",
  };
  const key = (status || "").toLowerCase();
  return <span className={`adb-badge ${map[key] || "adb-badge--grey"}`}>{status || "Unknown"}</span>;
};

const SettlementRunStatusBadge = ({ status }) => {
  const map = {
    paid: "stl-badge--green",
    completed: "stl-badge--green",
    active: "stl-badge--green",
    pending: "stl-badge--orange",
    upcoming: "stl-badge--blue",
    failed: "stl-badge--red",
  };
  const key = (status || "").toLowerCase();
  return <span className={`stl-badge ${map[key] || "stl-badge--grey"}`}>{status || "Unknown"}</span>;
};

const DashStatCard = ({ title, icon, iconBg, iconColor, value, deltaPct, deltaLabel, period }) => {
  const getDeltaLabel = (period) => {
    const labels = {
      weekly: "vs last week",
      monthly: "vs last month", 
      yearly: "vs last year",
      all_time: "all time",
      custom: "vs previous period",
    };
    return labels[period] || "vs last period";
  };

  return (
    <div className="rp-card adb-stat-card">
      <div className="adb-stat-card__top">
        <span className="adb-stat-card__icon" style={{ background: iconBg, color: iconColor }}>{icon}</span>
        <span className="adb-stat-card__title">{title}</span>
      </div>
      <div className="adb-stat-card__value">{value}</div>
      {deltaPct !== null ? (
        <span className={`adb-stat-card__delta ${deltaPct >= 0 ? "adb-stat-card__delta--up" : "adb-stat-card__delta--down"}`}>
          {deltaPct >= 0 ? "↑" : "↓"} {Math.abs(deltaPct)}% {getDeltaLabel(period)}
        </span>
      ) : deltaLabel ? (
        <span className="adb-stat-card__delta adb-stat-card__delta--muted">{deltaLabel}</span>
      ) : null}
    </div>
  );
};

const ChartCardPending = ({ title, period, onPeriodChange, note, graphData, dataKey, chartType = "line", description = "Historical performance trends over time." }) => {
  const [open, setOpen] = useState(false);
  
  const chartData = useMemo(() => {
    if (!graphData || !Array.isArray(graphData)) return null;
    
    const labels = graphData.map(item => item.label || "");
    const data = graphData.map(item => {
      const value = item[dataKey];
      if (typeof value === "object" && value !== null) {
        return value.amount || 0;
      }
      return value || 0;
    });
    
    return {
      labels,
      datasets: [
        {
          label: title,
          data,
          borderColor: chartType === "line" ? "#ff7a00" : undefined,
          backgroundColor: chartType === "line" ? "rgba(255, 122, 0, 0.1)" : "rgba(255, 122, 0, 0.8)",
          borderWidth: 2,
          fill: chartType === "line",
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [graphData, dataKey, title, chartType]);
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 13, weight: "bold" },
        bodyFont: { size: 12 },
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: $${context.raw.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { 
          font: { size: 11 }, 
          maxRotation: 45, 
          minRotation: 45,
          maxTicksLimit: 6,
        },
      },
      y: {
        grid: { color: "rgba(0, 0, 0, 0.05)" },
        ticks: { font: { size: 11 }, callback: (value) => `$${value.toLocaleString()}` },
        beginAtZero: true,
      },
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10,
      },
    },
  };
  
  if (!chartData) {
    return (
      <div className="rp-card adb-chart-card">
        <div className="rp-card__header">
          <div>
            <h2 className="rp-card__title">{title}</h2>
            <p className="rp-card__subtitle">{description}</p>
          </div>
          <Dropdown isOpen={open} toggle={() => setOpen((o) => !o)}>
            <DropdownToggle tag="div">
              <button className="adb-mini-period-btn">{getPeriodLabel(period)} <IconChevronDown /></button>
            </DropdownToggle>
            <DropdownMenu end>
              {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                <DropdownItem key={key} active={period === key} onClick={() => onPeriodChange(key)}>{label}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
        <div className="adb-chart-pending">
          <p className="ar-pending__text">{note}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="rp-card adb-chart-card">
      <div className="rp-card__header">
        <div>
          <h2 className="rp-card__title">{title}</h2>
          <p className="rp-card__subtitle">{description}</p>
        </div>
        <Dropdown isOpen={open} toggle={() => setOpen((o) => !o)}>
          <DropdownToggle tag="div">
            <button className="adb-mini-period-btn">{getPeriodLabel(period)} <IconChevronDown /></button>
          </DropdownToggle>
          <DropdownMenu end>
            {Object.entries(PERIOD_LABELS).map(([key, label]) => (
              <DropdownItem key={key} active={period === key} onClick={() => onPeriodChange(key)}>{label}</DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>
      <div style={{ height: "220px", padding: "10px", position: "relative", width: "100%" }}>
        {chartType === "line" ? (
          <Line data={chartData} options={options} />
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

const AdminDashboardPage = ({ onViewRedemptions, onViewCustomers, onViewAllRedemptions, onViewAllCustomers }) => {
  document.title = "Dashboard | Omaha Food Lovers";
  const navigate = useNavigate();

  const getCurrentUserId = () => {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const user = JSON.parse(authUser);
        return user.id || user.user_id || user.email;
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  const userId = getCurrentUserId();

  const [period, setPeriod] = useState("weekly");
  const [dateRange, setDateRange] = useState({ preset: "custom", start: "", end: "" });
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allBalanceRows, setAllBalanceRows] = useState([]);

  const withPeriodParams = (base) => {
    const params = new URLSearchParams();
    params.append("period", period);
    if (period === "custom") {
      if (dateRange?.start) params.append("date_from", dateRange.start);
      if (dateRange?.end) params.append("date_to", dateRange.end);
    }
    return `${base}?${params.toString()}`;
  };

  /* ---- existing endpoints (kept from the previous version) ---- */

  const { data: listingCountsData } = useQuery({
    queryKey: ["adminDashboardListingCounts", userId, period, dateRange],
    queryFn: () => get(withPeriodParams(endpoints.adminDashboardListingCounts)),
    staleTime: 5 * 60 * 1000,
  });

  const { data: linkedCardsData } = useQuery({
    queryKey: ["adminDashboardLinkedCardsStat", userId, period, dateRange],
    queryFn: () => get(withPeriodParams(endpoints.adminDashboardLinkedCardsStat)),
    staleTime: 5 * 60 * 1000,
  });

  const { data: partnerPerformanceBreakdownData } = useQuery({
    queryKey: ["adminDashboardPartnerPerformanceBreakdown", userId, period, dateRange],
    queryFn: () => get(withPeriodParams(endpoints.adminDashboardPartnerPerformanceBreakdown)),
    staleTime: 5 * 60 * 1000,
  });

  const { data: settlementDashboardData, isLoading: settlementDashboardLoading } = useQuery({
    queryKey: ["adminSettlementsDashboard", userId, period, dateRange],
    queryFn: () => get(withPeriodParams(endpoints.adminSettlementsDashboard)),
    staleTime: 5 * 60 * 1000,
  });

  /* ---- endpoints that already exist elsewhere in the app, reused here ---- */

  const { data: platformFeesData } = useQuery({
    queryKey: ["adminRestaurantsPlatformFeesSummaryByStatus", userId, period, dateRange],
    queryFn: () => get(withPeriodParams(endpoints.adminRestaurantsPlatformFeesSummaryByStatus)),
    staleTime: 5 * 60 * 1000,
  });

  const { data: recentActivityData, isLoading: recentActivityLoading } = useQuery({
    queryKey: ["adminActivityLogs", userId],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("page", 1);
      params.append("per_page", 5);
      return get(`${endpoints.adminActivityLogs}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ["adminSettlementInvoices", userId, page, search, status, period, dateRange],
    queryFn: () => {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("per_page", 20);
      params.append("sort_by", "invoice_number");
      params.append("sort_direction", "desc");
      if (search.trim()) params.append("search", search.trim());
      if (status) params.append("status", status);
      params.append("period", period);
      if (period === "custom") {
        if (dateRange?.start) params.append("date_from", dateRange.start);
        if (dateRange?.end) params.append("date_to", dateRange.end);
      }
      return get(`${endpoints.adminSettlementInvoices}?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });



  const listingPayload = extractPayload(listingCountsData) || {};
  const linkedPayload = extractPayload(linkedCardsData) || {};
  const breakdownPayload = extractPayload(partnerPerformanceBreakdownData);
  const settlementPayload = useMemo(() => extractPayload(settlementDashboardData) || {}, [settlementDashboardData]);
  const feesPayload = extractPayload(platformFeesData) || {};
  const activityPayload = extractPayload(recentActivityData);
  const invoicesPayload = useMemo(() => extractPayload(invoicesData) || {}, [invoicesData]);

  // Update hasMore based on pagination response
  useEffect(() => {
    if (invoicesPayload) {
      const currentPage = invoicesPayload.current_page || 1;
      const lastPage = invoicesPayload.last_page || 1;
      setHasMore(currentPage < lastPage);
    }
  }, [invoicesPayload]);

  // Update allBalanceRows when new data comes in
  useEffect(() => {
    const raw = pick(invoicesPayload, "data") || [];
    if (!Array.isArray(raw)) return;
    
    const newRows = raw.map((item) => ({
      id: item.id,
      restaurantName: pick(item, "restaurant.title", "restaurant.legal_business_name") || "Unknown",
      invoiceNumber: item.invoice_number,
      periodStart: pick(item, "period.start_date"),
      periodEnd: pick(item, "period.end_date"),
      memberSpend: pick(item, "performance_summary.eligible_member_spend", "settlement_calculation.eligible_member_spend"),
      platformFee: pick(item, "settlement_calculation.platform_fee_due", "invoice_total"),
      amountDue: item.amount_due,
      status: item.status,
    }));

    if (page === 1) {
      setAllBalanceRows(newRows);
    } else {
      setAllBalanceRows((prev) => [...prev, ...newRows]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicesPayload, page]);

  const filteredBalanceRows = useMemo(() => {
    let rows = allBalanceRows;
    if (status) rows = rows.filter((r) => r.status === status);
    return rows;
  }, [allBalanceRows, status]);

  /* ---- top 8 stat cards ---- */

  const totalMembers = pick(listingPayload, "users.Total_Users");
  const totalMembersDelta = pct(pick(listingPayload, "users.comparison_percentage"));

  const linkedCards = pick(linkedPayload, "value");
  const linkedCardsDelta = pct(pick(linkedPayload, "link_rate"));

  const totalPartners = pick(listingPayload, "businesses.Total_Restaurants");
  const totalPartnersDelta = pct(pick(listingPayload, "businesses.comparison_percentage"));
  const newPartnersThisMonth = pick(listingPayload, "businesses.Restaurants_This_Month");

  const summaryCards = pick(settlementPayload, "summary_cards") || {};
  const weeklyTransactions = pick(summaryCards, "total_member_spend.transaction_count");
  const weeklyMemberSpend = pick(summaryCards, "total_member_spend.amount");
  const platformRevenue = pick(summaryCards, "platform_fee.amount");
  const feeRatePct = pick(feesPayload, "fee_rate_pct", "platform_fee_rate") ?? 3;

  // TODO(api): confirm field names once the by-status breakdown includes
  // a "pending" and "paid"/"completed" bucket with count + amount.
  const statusBreakdown = pick(feesPayload, "statuses", "by_status", "status_breakdown", "breakdown") || [];
  const pendingStatus = statusBreakdown.find((s) => s.status === "pending" || s.status === "Pending");
  const completedStatus = statusBreakdown.find((s) => s.status === "completed" || s.status === "paid" || s.status === "Completed" || s.status === "Paid");

  const topRestaurants = useMemo(() => {
    const raw = breakdownPayload || [];
    if (!Array.isArray(raw)) return [];
    return raw.slice(0, 5).map((item) => ({
      id: item.restaurant_id,
      name: item.restaurant || "Unknown",
      spend: pick(item, "this_month.total_spend", "total_spend") || 0,
    }));
  }, [breakdownPayload]);

  const invoiceColumns = useMemo(() => [
    {
      key: "restaurant", label: "Restaurant", width: "1.6fr",
      render: (row) => (
        <span className="stl-table-restaurant">
          <span className="stl-table-restaurant__avatar">{row.restaurantName.slice(0, 1).toUpperCase()}</span>
          {row.restaurantName}
        </span>
      ),
    },
    { key: "invoiceNumber", label: "Invoice #", width: "1.2fr", render: (row) => row.invoiceNumber },
    { key: "period", label: "Period", width: "1.2fr", render: (row) => `${formatDateShort(row.periodStart)} – ${formatDateShort(row.periodEnd)}` },
    { key: "memberSpend", label: "Member Spend", width: "1.1fr", align: "right", render: (row) => currency(row.memberSpend) },
    { key: "platformFee", label: "Platform Fee", width: "1.1fr", align: "right", render: (row) => currency(row.platformFee) },
    { key: "amountDue", label: "Amount Due", width: "1.1fr", align: "right", render: (row) => currency(row.amountDue) },
    { key: "status", label: "Status", width: "0.9fr", align: "center", render: (row) => <StatusBadge status={row.status} /> },
    {
      key: "actions", label: "", width: "1.1fr", align: "right",
      render: (row) => (
        <span className="stl-row-actions">
          <SecondaryButton variant="light-grey" onClick={() => navigate(`/admin/settlements/invoices/${row.id}`)}>View</SecondaryButton>
        </span>
      ),
    },
  ], [navigate]);

  const handleLoadMore = () => {
    if (hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    setPage(1);
    setAllBalanceRows([]);
    setHasMore(true);
  };

  // Filter configuration for FilterToolbar
  const settlementFilters = [
    {
      key: "search",
      type: "search",
      label: "Search",
      value: search,
      onChange: (val) => {
        setSearch(val);
        setPage(1);
        setAllBalanceRows([]);
        setHasMore(true);
      },
      placeholder: "Search restaurant...",
    },
    {
      key: "period",
      type: "sortBy",
      label: "Period",
      value: period,
      onChange: (val) => {
        setPeriod(val);
        setPage(1);
        setAllBalanceRows([]);
        setHasMore(true);
        if (val !== "custom") {
          setDateRange({ preset: "custom", start: "", end: "" });
        }
      },
      defaultValue: "weekly",
      options: [
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "yearly", label: "Yearly" },
        { value: "all_time", label: "All Time" },
        { value: "custom", label: "Custom Range" },
      ],
    },
    {
      key: "status",
      type: "sortBy",
      label: "Status",
      value: status,
      onChange: (val) => {
        handleStatusChange(val);
      },
      defaultValue: "",
      options: [
        { value: "", label: "All Statuses" },
        { value: "pending", label: "Pending" },
        { value: "paid", label: "Paid" },
        { value: "cancelled", label: "Cancelled" },
      ],
    },
  ];

  if (period === "custom") {
    settlementFilters.push({
      key: "dateRange",
      type: "dateRange",
      label: "Date Range",
      value: dateRange,
      onChange: (val) => {
        setDateRange(val);
        setPage(1);
        setAllBalanceRows([]);
        setHasMore(true);
      },
      defaultValue: { preset: "custom", start: "", end: "" },
      hidePresets: true,
    });
  }

  /* ---- Recent Settlement Runs ---- */

  const recentRunsColumns = useMemo(() => [
    { key: "period", label: "Period", width: "1.5fr", render: (row) => row.periodLabel },
    { key: "status", label: "Status", width: "1fr", align: "center", render: (row) => <SettlementRunStatusBadge status={row.status} /> },
    { key: "fundingCollected", label: "Funding Collected", width: "1.2fr", align: "right", render: (row) => currency(row.fundingCollected) },
    { key: "payouts", label: "Payouts", width: "1.2fr", align: "right", render: (row) => currency(row.payouts) },
    { key: "amount", label: "Platform Revenue", width: "1.2fr", align: "right", render: (row) => currency(row.amount) },
    { key: "method", label: "Method", width: "0.8fr", align: "center", render: () => <span className="stl-ach-tag">ACH</span> },
  ], []);

  const recentRunsRows = useMemo(() => {
    const raw = pick(settlementPayload, "graph") || [];
    if (!Array.isArray(raw)) return [];
    return raw.slice(-5).reverse().map((item, i) => ({
      id: item.label || i,
      periodLabel: item.label || "N/A",
      status: item.completed_settlement_count > 0 ? "completed" : "pending",
      amount: item.platform_fee?.amount || 0,
      periodEnd: item.end_date,
      fundingCollected: item.total_member_spend?.amount || 0,
      payouts: item.net_restaurant_revenue?.amount || 0,
    }));
  }, [settlementPayload]);

  /* ---- Recent Activity ---- */

  const ACTIVITY_ICON = {
    api_login: { icon: starIcon, tone: "teal" },
    default: { icon: coinIcon, tone: "blue" },
  };

  const getActivityIcon = (activity) => ACTIVITY_ICON[activity?.event] || ACTIVITY_ICON.default;

  const recentActivityColumns = [
    {
      key: "description",
      label: "Activity",
      width: "3fr",
      render: (row) => {
        const { icon, tone } = getActivityIcon(row);
        return (
          <span className="data-table__row-label" style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>
            <IconAvatar icon={icon} tone={tone} size={32} />
            <span style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
              {cleanActivityText(row.description, "Activity recorded")}
            </span>
          </span>
        );
      },
    },
    {
      key: "type",
      label: "Type",
      width: "1fr",
      render: (row) => cleanActivityText(row.type, "Activity"),
    },
    {
      key: "time",
      label: "Date",
      width: "1fr",
      render: (row) => row.time || "Unknown",
    },
  ];

  const recentActivityRows = useMemo(() => {
    const raw = activityPayload?.data || activityPayload || [];
    const activities = Array.isArray(raw) ? raw : (raw.data || []);
    if (!Array.isArray(activities)) return [];
    return activities.slice(0, 5).map((item, i) => ({
      id: item.id || i,
      description: cleanActivityText(pick(item, "description", "message"), "Activity recorded"),
      type: cleanActivityText(pick(item, "type"), "Activity"),
      time: pick(item, "date", "created_at", "timestamp") || "—",
      event: pick(item, "event"),
    }));
  }, [activityPayload]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="adb-page">
          {/* ---- Header ---- */}
          <div className="adb-header">
            <div>
              <h1 className="adb-header__title">Dashboard</h1>
              <p className="adb-header__subtitle">Monitor platform performance, track key metrics, and manage system operations.</p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Dropdown isOpen={periodMenuOpen} toggle={() => setPeriodMenuOpen((o) => !o)}>
                <DropdownToggle tag="div">
                  <button className="adb-period-btn"><IconCalendar /> {getPeriodLabel(period)} <IconChevronDown /></button>
                </DropdownToggle>
                <DropdownMenu end>
                  {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                    <DropdownItem key={key} active={period === key} onClick={() => { setPeriod(key); setPage(1); if (key !== "custom") setDateRange({ preset: "custom", start: "", end: "" }); }}>{label}</DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
              {period === "custom" && (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <span style={{ color: "#6b7280" }}>to</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* ---- 8 top stat cards ---- */}
          <div className="adb-stats-grid">
            <DashStatCard title="Total Members" icon={<IconUsers />} iconBg="var(--bg-light-blue)" iconColor="var(--bg-dark-blue)" value={number(totalMembers)} deltaPct={totalMembersDelta} period={period} />
            <DashStatCard title="Linked Cards" icon={<IconCard />} iconBg="var(--bg-sea-green)" iconColor="#1ba97f" value={number(linkedCards)} deltaPct={linkedCardsDelta} period={period} />
            <DashStatCard title="Partner Restaurants" icon={<IconBuilding />} iconBg="#f1ecfc" iconColor="#7b4fd6" value={number(totalPartners)} deltaPct={totalPartnersDelta} deltaLabel={newPartnersThisMonth !== null ? `${number(newPartnersThisMonth)} new this month` : undefined} period={period} />
            <DashStatCard title="Transactions" icon={<IconReceipt />} iconBg="#eaf1fd" iconColor="var(--bg-dark-blue)" value={number(weeklyTransactions)} deltaPct={null} period={period} />
            <DashStatCard title="Member Spend" icon={<IconWallet />} iconBg="var(--bg-light-red)" iconColor="var(--text-orange)" value={currency(weeklyMemberSpend)} deltaPct={null} period={period} />
            <DashStatCard title={`Platform Revenue (${feeRatePct}%)`} icon={<IconPercent />} iconBg="#eaf1fd" iconColor="var(--bg-dark-blue)" value={currency(platformRevenue)} deltaPct={null} period={period} />
            <DashStatCard title="Pending Settlements" icon={<IconClock />} iconBg="var(--bg-light-red)" iconColor="var(--text-red)" value={pendingStatus ? number(pick(pendingStatus, "count", "total")) : "—"} deltaPct={null} deltaLabel={pendingStatus ? currency(pick(pendingStatus, "amount")) : "Awaiting status breakdown"} period={period} />
            <DashStatCard title="Completed Settlements" icon={<IconCheckCircle />} iconBg="var(--bg-sea-green)" iconColor="#1ba97f" value={completedStatus ? number(pick(completedStatus, "count", "total")) : "—"} deltaPct={null} deltaLabel={completedStatus ? currency(pick(completedStatus, "amount")) : "Awaiting status breakdown"} period={period} />
          </div>

          {/* ---- First row: 2 trend charts ---- */}
          <div className="adb-charts-grid-row">
            <ChartCardPending
              title={`Platform Revenue (${feeRatePct}%)`}
              period={period}
              onPeriodChange={setPeriod}
              note="A daily/weekly time-series endpoint isn't available yet — once it returns per-day platform fee totals, this renders as a bar chart matching the wireframe."
              description="Platform fee revenue collected from restaurant transactions."
              graphData={settlementPayload?.graph}
              dataKey="platform_fee"
              chartType="bar"
            />
            <ChartCardPending
              title="Member Spend"
              period={period}
              onPeriodChange={setPeriod}
              note="Same as Platform Revenue — needs a per-day member spend series to render as a line/area chart."
              description="Total member spending across all restaurants."
              graphData={settlementPayload?.graph}
              dataKey="total_member_spend"
              chartType="line"
            />
          </div>

          {/* ---- Second row: 1 chart + Top Restaurants ---- */}
          <div className="adb-charts-grid-row">
            <ChartCardPending
              title="Net Restaurant Revenue"
              period={period}
              onPeriodChange={setPeriod}
              note="Needs a per-day transaction-count series to render as a line chart."
              description="Net revenue earned by restaurants after platform fees."
              graphData={settlementPayload?.graph}
              dataKey="net_restaurant_revenue"
              chartType="line"
            />
            <div className="rp-card adb-top-restaurants-card">
              <div className="rp-card__header">
                <div>
                  <h2 className="rp-card__title">Top Restaurants by Spend</h2>
                  <p className="rp-card__subtitle">Restaurants with the highest member spending this period.</p>
                </div>
                <SecondaryButton
                  type="button"
                  variant="orange"
                  icon={WhiteArrow}
                  onClick={() => navigate("/admin/restaurants")}
                >
                  View All
                </SecondaryButton>
              </div>
              {topRestaurants.length === 0 ? (
                <div className="adb-empty"><p className="ar-pending__text">No restaurant spend data yet.</p></div>
              ) : (
                <div className="adb-top-restaurants-list">
                  {topRestaurants.map((r, i) => (
                    <div key={r.id} className="adb-top-restaurant-row">
                      <span className="adb-top-restaurant-row__rank">{i + 1}</span>
                      <span className="adb-top-restaurant-row__avatar">{r.name.slice(0, 1).toUpperCase()}</span>
                      <span className="adb-top-restaurant-row__name">{r.name}</span>
                      <span className="adb-top-restaurant-row__amount">{currency(r.spend)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ---- Restaurant Settlement Summary ---- */}
          <div className="rp-card adb-card">
            <div className="rp-card__header adb-table-header">
              <div>
                <h2 className="rp-card__title">Restaurant Settlement Summary <span className="adb-muted-title">({getPeriodLabel(period)})</span> <IconInfo /></h2>
                <p className="rp-card__subtitle">Overview of restaurant settlements and payment status.</p>
              </div>
              <FilterToolbar filters={settlementFilters} />
            </div>

            <div className="ar-table-scroll" style={{ overflowX: "auto" }}>
              <DataTable
                columns={invoiceColumns}
                rows={filteredBalanceRows}
                getRowKey={(r) => r.id}
                loading={invoicesLoading}
                loadingMore={invoicesLoading && page > 1}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                compact={true}
              />
            </div>
          </div>

          {/* ---- Recent Runs / Recent Activity ---- */}
          
          {/* Recent Settlement Runs */}
          <div className="rp-card adb-card">
            <div className="rp-card__header">
              <div>
                <h2 className="rp-card__title">Recent Settlement Runs</h2>
                <p className="rp-card__subtitle">Latest settlement processing runs and their status.</p>
              </div>
              <SecondaryButton
                type="button"
                variant="orange"
                icon={WhiteArrow}
                onClick={() => navigate("/admin/settlements")}
              >
                View All
              </SecondaryButton>
            </div>
            {recentRunsRows.length === 0 && !settlementDashboardLoading ? (
              <div className="ar-pending"><p className="ar-pending__text">No settlement runs recorded yet.</p></div>
            ) : (
              <div className="ar-table-scroll" style={{ overflowX: "auto", width: "100%" }}>
                <DataTable
                  columns={recentRunsColumns}
                  rows={recentRunsRows}
                  getRowKey={(r) => r.id}
                  loading={settlementDashboardLoading}
                  compact
                />
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="rp-card adb-card">
            <div className="rp-card__header">
              <div>
                <h2 className="rp-card__title">Recent Activity</h2>
                <p className="rp-card__subtitle">Latest admin activities and system events.</p>
              </div>
              <SecondaryButton
                type="button"
                variant="orange"
                icon={WhiteArrow}
                onClick={() => navigate("/admin/profile/activity-logs")}
              >
                View All
              </SecondaryButton>
            </div>
            <DataTable columns={recentActivityColumns} rows={recentActivityRows} getRowKey={(r) => r.id} loading={recentActivityLoading} compact />
          </div>
        </div>
      </Container>
    </div>
  );
};

AdminDashboardPage.propTypes = {
  onViewRedemptions: PropTypes.func,
  onViewCustomers: PropTypes.func,
  onViewAllRedemptions: PropTypes.func,
  onViewAllCustomers: PropTypes.func,
};

AdminDashboardPage.defaultProps = {
  onViewRedemptions: () => {},
  onViewCustomers: () => {},
  onViewAllRedemptions: () => {},
  onViewAllCustomers: () => {},
};

export default AdminDashboardPage;
